import React, { useCallback, useEffect, useState } from "react";
import { v4, validate } from "uuid";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router";
import { FormProvider, SubmitHandler } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { Button, Col, FormGroup } from "react-bootstrap";
import { SectionHeading } from "../../../components/section-heading";
import ContentWrapper from "../../../components/content-wrapper";
import { FullWidthLoader } from "../../../components/full-width-loader";
import { useProperty } from "../../../components/property-provider";
import { ActiveSwitchComponent } from "../components/ActiveSwitchComponent";
import { TickerComponent } from "../components/TickerComponent";
import { TranslateComponent } from "../components/TranslateComponent";
import { Typology } from "../components/typology";
import { ValidityWindowComponent } from "../components/ValidityWindowComponent";
import { RestrictionComponent } from "../components/RestrictionComponent";
import { InventoryLinesComponent } from "../components/InventoryLinesComponent";
import { useFormData } from "../hooks/formData.hook";
import { useModal } from "../hooks/modal.hook";
import { useMode } from "../hooks/mode.hook";
import { InsuranceEntity } from "../entity";
import { StyledCard, StyledBackButton } from "../styles";
import { messages } from "../messages";
import { StyledSpinner } from "../../witadmin/CRUD/EntityForm/EntityForm";
import { ForbiddenSection, useAuthorizedActions } from "../../../components/authorization-verifier";
import { SECTION } from "../../../lib/constants";
import { useInsuranceApi } from "../hooks/insuranceApi.hook";

export function InsuranceEdit() {
  const authorizedActions = useAuthorizedActions(SECTION.INSURANCES);
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();
  const property = useProperty();
  const { methods, update } = useFormData();
  const { mode, setMode, title } = useMode();
  const { showModal } = useModal();
  const { onCreate, onUpdate, onGetOne } = useInsuranceApi();

  const [pageStatus, setPageStatus] = useState<"loading" | "processing" | "ready">("loading");

  const handleOnBack = useCallback(() => {
    history.push(`/${property.ticker}/insurances`);
  }, [history, property.ticker]);

  const handleUpdate = useCallback(async () => {
    setPageStatus("processing");
    try {
      await onUpdate(methods.getValues());
      await showModal("update_success", handleOnBack);
    } catch (error) {
      await showModal("update_error");
    } finally {
      setPageStatus("ready");
    }
  }, [onUpdate, methods, showModal, handleOnBack]);

  const handleCreate = useCallback(async () => {
    setPageStatus("processing");
    try {
      await onCreate(methods.getValues());
      await showModal("create_success", handleOnBack);
    } catch (error) {
      await showModal("create_error");
    } finally {
      setPageStatus("ready");
    }
  }, [methods, onCreate, showModal, handleOnBack]);

  const onSubmit: SubmitHandler<InsuranceEntity> = useCallback(async () => {
    switch (mode) {
      case "edit":
        return await handleUpdate();
      case "duplicate":
      case "add":
      default:
        return await handleCreate();
    }
  }, [handleCreate, handleUpdate, mode]);

  useEffect(() => {
    const id = location.pathname.split("/").pop();
    if (id && validate(id)) {
      const [action] = location.pathname.split("/").slice(-2, -1);
      switch (action) {
        case "insurance-copy":
          setMode("duplicate");
          return void onGetOne(id)
            .then((insurance: InsuranceEntity) => update(insurance.copy({ id: v4(), ticker: "" })))
            .catch(async () => {
              await showModal("get_error", handleOnBack);
            })
            .finally(() => setPageStatus("ready"));
        case "insurance-edit":
        default:
          setMode("edit");
          return void onGetOne(id)
            .then((insurance: InsuranceEntity) => update(insurance))
            .catch(async () => {
              await showModal("get_error", handleOnBack);
            })
            .finally(() => setPageStatus("ready"));
      }
    }
    setPageStatus("ready");
    return setMode("add");
  }, [handleOnBack, location.pathname, onGetOne, setMode, showModal, update]);

  if (!authorizedActions.modify) {
    return <ForbiddenSection />;
  }

  if (pageStatus === "loading") {
    return <FullWidthLoader />;
  }

  return (
    <ContentWrapper>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <SectionHeading>
            <FormattedMessage {...title} />
          </SectionHeading>
          <StyledCard>
            <ActiveSwitchComponent {...methods} />
            <TickerComponent {...methods} readOnly={mode === "edit"} />
            <TranslateComponent {...methods} />
            <Typology {...methods}>
              <Typology.TaxationComponent {...methods} />
              <Typology.SectionComponent mode={mode} {...methods} />
            </Typology>
            <ValidityWindowComponent {...methods} />
            <RestrictionComponent {...methods} />
            <InventoryLinesComponent {...methods} />

            <FormGroup as={Col}>
              <Col sm={3}>
                <Button
                  variant="primary"
                  className="mt-3 mr-3"
                  type="submit"
                  disabled={pageStatus === "processing"}>
                  {pageStatus === "processing" ? (
                    <StyledSpinner className="fa fa-spinner fa-spin" />
                  ) : (
                    <FormattedMessage {...messages.save} />
                  )}
                </Button>
                <StyledBackButton className="mt-3" onClick={handleOnBack}>
                  {intl.formatMessage(messages.goBack)}
                </StyledBackButton>
              </Col>
            </FormGroup>
          </StyledCard>
        </form>
      </FormProvider>
    </ContentWrapper>
  );
}
